<template>
    <div class="report-ndvi">
        <div class="report-container">
            <router-link
                class="report-container__btn btn btn-primary btn--rounded btn--flat"
                tag="a"
                to="/relatorios"
            >
                <i class="fa fa-arrow-left"/>
                <span>
                    Voltar
                </span>
            </router-link>
            <h2 class="report-container__title">
                Relatório NDVIs
            </h2>
        </div>
        <div class="filter-container">
            <div class="filter-container__item">
                <label class="app-label">
                    Período
                </label>
                <period-select-picker
                    :selectedInterval="selectedInterval"
                    @intervalSelected="selectedInterval = $event.interval"
                />
            </div>
            <div class="filter-container__item filter-container__item--multiple-selector">
                <multiple-selector
                    label="Talhão(ões)"
                    placeholder="Selecione o(s) talhão(ões)"
                    :items="allFields"
                    item-text="name"
                    item-value="_id"
                    v-model="selectedFields"
                />
            </div>
            <div class="filter-container__item">
                <label class="app-label">
                    NDVIs por página
                </label>
                <select
                    class="app-select"
                    v-model="selectedFormat"
                >
                    <option value="block">
                        1 (uma)
                    </option>
                    <option value="grid">
                        +1 (organizadas por talhão)
                    </option>
                </select>
           </div>
            <div class="filter-container__item filter-container__date">
                <date-picker
                    :disabled="!selectedCustom"
                    :dataSent="periodSend.startDate"
                    @dateSelected="periodSend.startDate = $event.date"
                    label="Data inicial"
                />
                <date-picker
                    :disabled="!selectedCustom"
                    :dataSent="periodSend.endDate"
                    @dateSelected="periodSend.endDate = $event.date"
                    label="Data final"
                />
            </div>
            <div class="filter-container__item filter-container__action">
                <button
                    class="btn btn-primary--outlined btn--flat"
                    @click="generateReport"
                >
                    <span
                        v-if="loadingNdvis"
                        class="spinner-border spinner-border-sm"
                        role="status"
                    />
                    <span v-else>
                        <i class="fa fa-download mr-2"/>
                        <span>
                            Gerar relatorio
                        </span>
                    </span>
                </button>
                <button
                    class="btn btn-primary--outlined btn--flat"
                    :disabled="imagesNDVIOrdered.length == 0"
                    id="fazenda"
                    @click="generatePDF()"
                >
                    <span
                        v-if="loadingPDF"
                        class="spinner-border spinner-border-sm" role="status"
                    />
                    <span v-else>
                        <i class="fa fa-file-pdf mr-2"/>
                        <span>
                            Baixar PDF
                        </span>
                    </span>
                </button>
            </div>
            <br/>
            <div class="filter-container__item filter-container__item--hint-text">
                Desmarque a seleção dos mapas que você não deseja ver no relatório.
            </div>
        </div>
        <div
            class="card-container"
            v-show="imagesNDVIOrdered.length > 0"
        >
            <div
                class="col-12 col-sm-6 col-md-4 col-lg-3 p-2"
                v-for="ndvi in imagesNDVIOrdered"
                :key="ndvi._id"
            >
                <image-card-ndvi
                    :ndvi="ndvi"
                    @activateOrDeactivateNdvi="activateOrDeactivateNdvi(ndvi)"
                />
            </div>
        </div>
        <div
            class="no-content"
            v-show="!loadingNdvis && imagesNDVIOrdered.length < 1"
        >
            <i class="fa fa-exclamation-circle fa-2x"/>
            <span class="">
                Sem imagens para esse talhão
            </span>
        </div>
    </div>
</template>

<script>
import DatePicker from "@/components/commons/DatePicker.vue";
import ImageCardNdvi from "@/components/reports/ndvis/ImageCardNdvi.vue";
import PeriodSelectPicker from "@/components/commons/PeriodSelectPicker.vue";
import MultipleSelector from '@/components/commons/MultipleSelector.vue';
import Ndvi from "@/api/Ndvi";
import { calculatePeriodInterval } from "@/utils/calculatePeriodInterval";
import { mapGetters, mapActions } from "vuex";

export default {
    name: 'ReportNDVIs',
  components: {
    DatePicker,
    ImageCardNdvi,
    PeriodSelectPicker,
    MultipleSelector
  },
  data() {
    return {
        loadingNdvis: false,
        loadingPDF: false,
        selectedInterval: "weekly",
        selectedFormat: "grid",
        periodSend: {
            custom: false,
            startDate: "",
            endDate: "",
        },
        selectedFields: [],
        imagesNDVIOrdered: [],
        ndviReportPayload: {
            propertyId: "",
            cropId: "",
            startDate: "",
            endDate: "",
            fieldIdList: []
        }
    };
  },

  async mounted() {
    this.selectedInterval = "weekly"
    await this.updateFilter()
  },

  computed: {
    ...mapGetters("property", [
      "getCropSelected",
      "getPropertySelected",
    ]),
    ...mapGetters("fields", [
        "allFields"
    ]),
    ...mapGetters("ndvis", [
        "getAllNdvis"
    ]),
    selectedCustom(){
        return this.selectedInterval == 'custom'
    }
  },
  methods: {
    ...mapActions("fields", [
        "getFieldsByProperty",
    ]),
    ...mapActions("ndvis", [
        "findNdvis",
        "updateNdvi"
    ]),
    updatePayload(){
        const payload = {
            propertyId: this.getPropertySelected._id,
            cropId: this.getCropSelected._id,
            fieldIdList: this.selectedFields,
            startDate: this.periodSend.startDate,
            endDate: this.periodSend.endDate,
            template: this.selectedFormat
        }
        return payload
    },
    async updateFilter(){
        this.periodSend = calculatePeriodInterval(this.selectedInterval, this.getCropSelected)
        const payload = this.updatePayload()
        await this.getFieldsByProperty(payload)
    },
    async generateReport() {
        try {
            this.loadingNdvis = true

            const payload = this.updatePayload()
            this.validateFilter(payload)

            await this.findNdvis(payload)
            this.imagesNDVIOrdered = this.getAllNdvis
            this.savedPayload = { ...payload }

            this.loadingNdvis = false
        } catch (error) {
            this.loadingNdvis = false
            this.$vToastify.error(error.message, "NDVI")
        }
    },
    validateFilter({ startDate, endDate }) {
        if (!endDate) {
            throw new Error("Informe a data final");
        }
        if (!startDate) {
            throw new Error("Informe a data inicial");
        }
        if (new Date(endDate) < new Date(startDate)) {
            throw new Error("A data final não pode ser menor que a inicial");
        }
        if (this.dateDiffInDays(new Date(startDate), new Date(endDate)) > 180) {
            throw new Error("O intervalo entre das datas não deve ser maior que 6 meses");
        }
    },
    async generatePDF() {
        try {
            this.loadingPDF = true

            this.validateFilter(this.savedPayload)

            const { propertyId, cropId, fieldIdList, startDate, endDate, template } = this.savedPayload
            const response = await Ndvi.getNdviReport(propertyId, cropId, fieldIdList, startDate, endDate, template)

            if (response.status === 200 && response.data.pdf) {
                this.$vToastify.success('PDF gerado com sucesso!')
                this.goToPage(response.data.pdf)
            }

            this.loadingPDF = false
        } catch (error) {
            this.loadingPDF = false
            this.$vToastify.error(error.message, "NDVI")
        }
    },
    async activateOrDeactivateNdvi(ndvi) {
        await this.updateNdvi({ ndviId: ndvi._id, ndvi: { active: !!ndvi.active } });
    },
    goToPage(url) {
        window.open(url, '_blank')
    }
  },
  watch: {
    async getPropertySelected() {
        await this.updateFilter()
    },
    async getCropSelected() {
        await this.updateFilter()
    },
    async selectedInterval(){
        await this.updateFilter()
    }
  },
};
</script>

<style scoped lang="sass">
.report-ndvi
    height: 100%
    grid-gap: 10px

    .report-container
        display: flex
        align-items: center
        justify-content: center
        position: relative
        padding: 20px 0
        width: 100%

        &__btn
            position: absolute
            left: 0

        &__title
            font-size: 16px
            margin: 0

    .filter-container
        display: flex
        justify-content: center
        align-items: flex-end
        padding: 10px 0
        flex-wrap: wrap
        border: $border
        border-radius: $border-radius

        &__item
            flex-grow: 1
            padding: 10px

            &--hint-text
                width: 100%
                color: $color-blue



        &__action
            display: flex
            grid-gap: 10px
            justify-content: center
            min-width: 350px


        &__date
            display: flex
            grid-gap: 10px
            min-width: 350px

            .datePicker
                width: 100%

            #date
                height: 40px

    .card-container
        display: flex
        flex-wrap: wrap
        padding: 10px 0

    .no-content
        display: flex
        justify-content: center
        align-items: center
        height: 100px
        color: $color-grey
        grid-gap: 10px
</style>
